/**
 * @fileoverview Multi-CRM input component for doctors with multiple CRM registrations
 */
import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { intl } from "dmsbasereact";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import FlagIcon from "@mui/icons-material/Flag";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import { getBrazilianStateLabel, getBrazilianStateOptions } from "../../constants/BrazilianStates";

/**
 * Component for managing multiple CRM entries
 *
 * @param {Array} crms - Array of CRM objects [{crm: string, crm_state: string}]
 * @param {Function} onChange - Callback when CRMs change
 * @param {boolean} disabled - Whether inputs are disabled
 * @param {boolean} required - Whether at least one CRM is required
 */
export const MultiCrmInput = ({ crms = [],
  onChange,
  disabled = false,
  required = false}) => {
  const [newCrm, setNewCrm] = useState("");
  const [newCrmState, setNewCrmState] = useState("");

  const handleAddCrm = useCallback(() => {
    if (!newCrm.trim() || !newCrmState) return;

    // Check for duplicates
    const isDuplicate = crms.some(
      (item) => item.crm === newCrm.trim() && item.crm_state === newCrmState
    );

    if (isDuplicate) return;

    const updatedCrms = [...crms, { crm: newCrm.trim(), crm_state: newCrmState }];
    onChange(updatedCrms);
    setNewCrm("");
    setNewCrmState("");
  }, [newCrm, newCrmState, crms, onChange]);

  const handleRemoveCrm = useCallback(
    (index) => {
      const updatedCrms = crms.filter((_, idx) => idx !== index);
      onChange(updatedCrms);
    },
    [crms, onChange]
  );

  const handleSetAsMain = useCallback(
    (index) => {
      if (index === 0) return;
      const updatedCrms = [...crms];
      const [promoted] = updatedCrms.splice(index, 1);
      updatedCrms.unshift(promoted);
      onChange(updatedCrms);
    },
    [crms, onChange]
  );

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && newCrm && newCrmState) {
      event.preventDefault();
      handleAddCrm();
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
        {intl.get("CRMRegistrations").d("CRM Registrations")}
        {required && " *"}
      </Typography>

      {/* Existing CRMs */}
      {crms.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {crms.map((item, index) => {
            const isMain = index === 0;
            return (
              <Paper
                key={`${item.crm}-${item.crm_state}`}
                variant="outlined"
                sx={{
                  p: 1,
                  px: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  borderLeft: 3,
                  borderLeftColor: "primary.main",
                  bgcolor: "grey.50",
                }}
              >
                {/* Flag icon to set as main */}
                {!disabled && (
                  <Tooltip
                    title={isMain
                      ? intl.get("MainCRM").d("Main CRM")
                      : intl.get("SetAsMainCRM").d("Set as Main CRM")}
                  >
                    <span>
                      <IconButton
                        size="small"
                        color={isMain ? "success" : "default"}
                        onClick={() => handleSetAsMain(index)}
                        disabled={isMain}
                        sx={{
                          p: 0.5,
                          "&.Mui-disabled": isMain ? { color: "success.main" } : {},
                        }}
                      >
                        {isMain ? <FlagIcon fontSize="small" /> : <OutlinedFlagIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Typography variant="body2" sx={{ flex: 1, fontWeight: isMain ? 500 : 400 }}>
                  {`${item.crm} (${getBrazilianStateLabel(item.crm_state)})`}
                </Typography>
                {isMain && (
                  <Chip
                    label={intl.get("MainCRM").d("Main CRM")}
                    color="success"
                    size="small"
                    variant="filled"
                  />
                )}
                {!disabled && (
                  <Tooltip title={intl.get("Tooltip.Action.Delete").d("Delete")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveCrm(index)}
                      sx={{ p: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Add new CRM */}
      <Grid container spacing={1} alignItems="flex-end">
        <Grid item xs={5}>
          <TextField
            label={intl.get("CRM").d("CRM")}
            value={newCrm}
            onChange={(e) => setNewCrm(e.target.value)}
            onKeyPress={handleKeyPress}
            fullWidth
            size="small"
            disabled={disabled}
            placeholder="123456"
            inputProps={{ maxLength: 10 }}
          />
        </Grid>
        <Grid item xs={5}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>{intl.get("State").d("State")}</InputLabel>
            <Select
              value={newCrmState}
              label={intl.get("State").d("State")}
              onChange={(e) => setNewCrmState(e.target.value)}
            >
              {getBrazilianStateOptions().map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <Tooltip title={intl.get("Tooltip.Action.Add").d("Add")}>
            <span>
              <IconButton
                color="primary"
                onClick={handleAddCrm}
                disabled={disabled || !newCrm.trim() || !newCrmState}
              >
                <AddCircleIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {required && crms.length === 0 && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {intl.get("AtLeastOneCrmRequired").d("At least one CRM is required")}
        </Typography>
      )}
    </Box>
  );
};

MultiCrmInput.propTypes = {
  crms: PropTypes.arrayOf(
    PropTypes.shape({
      crm: PropTypes.string.isRequired,
      crm_state: PropTypes.string.isRequired,
    })
  ),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};
